import { Alert, AlertTitle, Button, Typography } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import {
  generationFailedCopy,
  generationFailedTitles,
  type GenerationFailedCopyProps,
} from './generation-failed-copy.js'
import styles from './generation-failed-block.module.css'

export interface GenerationFailedBlockProps extends GenerationFailedCopyProps {
  className?: string
  /** Absent in a read-only editor: the Remove button is not rendered */
  onRemove?: () => void
}

/**
 * The state of a generated image or audio block whose generation was refused or failed
 * (GH #1958). Replaces the loading pulse, which otherwise ran forever on a stub that will
 * never get a URL. Inline and persistent — not a toast: the stub lives in a shared document,
 * so a toast would fire for every bandmate and vanish on reload.
 *
 * No retry: a quota refusal can only fail again, and the stub does not carry the prompt.
 * Remove needs no confirmation: nothing of value is lost.
 */
export const GenerationFailedBlock: FC<GenerationFailedBlockProps> = props => {
  const { className, kind, reason, requestedByMe, onRemove } = props

  return (
    <Alert
      severity="warning"
      variant="outlined"
      icon={false}
      role="status"
      className={cn(styles.root, className)}
      contentEditable={false}
    >
      <AlertTitle>{generationFailedTitles[kind]}</AlertTitle>
      <Typography variant="body2">{generationFailedCopy({ kind, reason, requestedByMe })}</Typography>
      {onRemove ? (
        <Button variant="text" size="small" className={styles.remove} onClick={onRemove}>
          Remove
        </Button>
      ) : null}
    </Alert>
  )
}
